import { Compass, Film, ListVideo, Search, UserRound, UsersRound } from "lucide-react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../auth";
import { track } from "../analytics";

const navItems = [
  { to: "/", label: "Discover", icon: Compass },
  { to: "/library", label: "Library", icon: Film },
  { to: "/lists", label: "Lists", icon: ListVideo },
  { to: "/people", label: "Friends", icon: UsersRound },
  { to: "/profile", label: "Profile", icon: UserRound },
];

export function Shell() {
  const { user, openSignIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "/" && document.activeElement?.tagName !== "INPUT") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/search") setQuery("");
  }, [location.pathname]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const cleaned = query.trim();
    if (cleaned.length >= 2) {
      track("search_submitted", { query_length: cleaned.length, source: "global_header" });
      navigate(`/search?q=${encodeURIComponent(cleaned)}`);
    }
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <aside className="side-rail">
        <NavLink to="/" className="wordmark" aria-label="My K-List home">
          <span className="wordmark-k">K</span>
          <span>list</span>
        </NavLink>
        <nav aria-label="Primary navigation">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`}>
              <Icon size={20} strokeWidth={1.8} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="side-footer">
          <span className="hangul-detail">나의 케이리스트</span>
          <span>Stories, kept close.</span>
          <span><NavLink to="/support">Support</NavLink> · <NavLink to="/privacy">Privacy</NavLink> · <NavLink to="/terms">Terms</NavLink></span>
        </div>
      </aside>

      <div className="app-column">
        <header className="topbar">
          <NavLink to="/" className="mobile-wordmark" aria-label="My K-List home">
            <span>K</span>list
          </NavLink>
          <form className="global-search" role="search" onSubmit={submitSearch}>
            <Search size={17} aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              name="global-search"
              autoComplete="off"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search Korean dramas and films…"
              aria-label="Search Korean dramas and films"
            />
            <kbd>/</kbd>
          </form>
          {user ? (
            <NavLink className="profile-pill" to="/profile">
              <span className="avatar small">{user.displayName.slice(0, 1).toUpperCase()}</span>
              <span className="profile-pill-name">{user.displayName}</span>
            </NavLink>
          ) : (
            <button className="quiet-button" onClick={openSignIn}>Sign in</button>
          )}
        </header>
        <main id="main-content" className="page-stage">
          <Outlet />
        </main>
      </div>

      <nav className="bottom-nav" aria-label="Mobile navigation">
        {navItems.slice(0, 5).map(({ to, label, icon: Icon }) => (
          <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => isActive ? "active" : ""}>
            <Icon size={20} strokeWidth={1.8} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
